/**
 * Full account-page Alpine components.
 * Push Account remains in app.js because it can be opened from every storefront page.
 */
document.addEventListener('alpine:init', () => {
  Alpine.data('newAccountPage', (config = {}) => ({
      page: 'overview',
      allowedViews: ['overview', 'orders','owned', 'benefits', 'offers', 'appointments', 'settings', 'faq'],
      loading: false,
      statusMessage: '',
      statusType: 'success',
      statusTimer: null,
      showBirthday: false,
      showPass: false,
      birthdayDay: '',
      birthdayMonth: '',
      previousFocus: null,
      popstateHandler: null,
      savedHandler: null,
      monthNames: [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ], 

      init() {
        if (this.$store.profile && config.profile) {
          this.$store.profile.init(config.profile);
        }

        const requestedView = new URLSearchParams(window.location.search).get('section');
        if (this.allowedViews.includes(requestedView)) {
          this.page = requestedView;
        }

        const birthday = this.$store.profile?.birthday || config.profile?.birthday || '';
        if (birthday.includes('-')) {
          const [month, day] = birthday.split('-');
          this.birthdayMonth = month || '';
          this.birthdayDay = day || '';
        }

        this.popstateHandler = () => {
          const view = new URLSearchParams(window.location.search).get('section') || 'overview';
          if (this.allowedViews.includes(view)) {
            this.page = view;
            this.$nextTick(() => this.focusCurrentHeading(false));
          }
        };

        this.savedHandler = (event) => {
          this.showStatus(event.detail?.message || 'Your details have been saved.');
        };

        window.addEventListener('popstate', this.popstateHandler);
        window.addEventListener('account-form:saved', this.savedHandler);

        this.$nextTick(() => {
          if (this.page === 'settings') {
            window.dispatchEvent(new CustomEvent('account-tab:open', { detail: { tab: 'details' } }));
          }
        });
      },

      destroy() {
        window.clearTimeout(this.statusTimer);
        window.removeEventListener('popstate', this.popstateHandler);
        window.removeEventListener('account-form:saved', this.savedHandler);
        this.unlockPage();
      },

      get profileCompleteCount() {
        const profile = this.$store.profile || {};
        return [
          Boolean(profile.birthday),
          Boolean(profile.appLoggedIn),
          Boolean(profile.emailSub),
          Boolean(profile.sms_sub)
        ].filter(Boolean).length;
      },

      get profilePercentage() {
        return Math.round((this.profileCompleteCount / 4) * 100);
      },

      get viewTitle() {
        return {
          overview: 'My account',
          orders: 'Your purchases',
          benefits: 'Your benefits',
          offers: 'Your personal offers',
          appointments: 'Your appointments',
          settings: 'Your details',
          faq: 'Help and FAQs'
        }[this.page] || 'My account';
      },

      goTo(view, options = {}) {
        if (!this.allowedViews.includes(view)) return;
        if (view === this.page && !options.force) return;

        this.page = view;
        const url = new URL(window.location.href);
        if (view === 'overview') {
          url.searchParams.delete('section');
        } else {
          url.searchParams.set('section', view);
        }

        if (options.replace) {
          window.history.replaceState({ accountView: view }, '', url.toString());
        } else {
          window.history.pushState({ accountView: view }, '', url.toString());
        }

        if (view === 'settings') {
          window.dispatchEvent(new CustomEvent('account-tab:open', { detail: { tab: 'details' } }));
        }

        this.$nextTick(() => {
          window.scrollTo({ top: 0, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
          this.focusCurrentHeading();
        });
      },

      focusCurrentHeading(preventScroll = true) {
        requestAnimationFrame(() => {
          const heading = this.$refs[`${this.page}Heading`];
          heading?.focus({ preventScroll });
        });
      },

      showStatus(message, type = 'success', duration = 4000) {
        window.clearTimeout(this.statusTimer);
        this.statusMessage = message;
        this.statusType = type;
        this.statusTimer = window.setTimeout(() => {
          this.statusMessage = '';
        }, duration);
      },

      async quickAction(action) {
        if (this.loading) return;

        if (action === 'birthday') {
          this.openBirthday();
          return;
        }

        if (action === 'app') {
          this.openApp();
          return;
        }

        if (action === 'email' && !config.email) {
          this.showStatus('Add an email address before subscribing.', 'error');
          this.goTo('settings');
          return;
        }

        if (action === 'sms' && !config.phone) {
          this.showStatus('Add a phone number before subscribing to SMS.', 'error');
          this.goTo('settings');
          return;
        }

        const extra = {
          subscribe_marketing: action === 'email' ? true : Boolean(this.$store.profile?.emailSub),
          subscribe_sms_marketing: action === 'sms' ? true : Boolean(this.$store.profile?.sms_sub)
        };

        const succeeded =
          await this.sendProfileWebhook(
            action,
            extra
          );

        if (!succeeded) return;

        /*
        * Track only a genuine transition
        * from unsubscribed to subscribed.
        *
        * These checks must happen before
        * the profile store is updated below.
        */
        if (
          action === 'email' &&
          !this.$store.profile?.emailSub
        ) {
          window.FAFAnalytics?.subscribe({
            leadType: 'email',
            email: config.email
          });
        }

        if (
          action === 'sms' &&
          !this.$store.profile?.sms_sub
        ) {
          window.FAFAnalytics?.subscribe({
            leadType: 'phone',
            email: config.email || '',
            phone: config.phone
          });
        }

        if (action === 'email') {
          this.$store.profile.emailSub = true;
        }

        if (action === 'sms') {
          this.$store.profile.sms_sub = true;
        }

        this.showStatus(
          action === 'email'
            ? 'Email updates are now enabled.'
            : 'SMS updates are now enabled.'
        );
      },

      async sendProfileWebhook(action, extra = {}) {
        if (!config.webhookUrl) {
          this.showStatus('This account action is temporarily unavailable.', 'error');
          return false;
        }

        this.loading = true;
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 12000);

        try {
          const response = await fetch(config.webhookUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            signal: controller.signal,
            body: JSON.stringify({
              action,
              source: 'new_account_dashboard',
              customer_id: config.customerId,
              token: config.token,
              email: config.email || '',
              phone: config.phone || '',
              birthday: this.$store.profile?.birthday || '',
              consented_at: new Date().toISOString(),
              ...extra
            })
          });

          if (!response.ok) {
            const message = await response.text().catch(() => '');
            throw new Error(message || `Request failed (${response.status})`);
          }

          return true;
        } catch (error) {
          const message = error?.name === 'AbortError'
            ? 'That took too long. Please try again.'
            : 'Sorry, that could not be saved. Please try again.';
          this.showStatus(message, 'error', 5000);
          return false;
        } finally {
          window.clearTimeout(timeout);
          this.loading = false;
        }
      },

      ordinal(number) {
        if ([11, 12, 13].includes(number)) return `${number}th`;
        const suffix = { 1: 'st', 2: 'nd', 3: 'rd' }[number % 10] || 'th';
        return `${number}${suffix}`;
      },

      birthdayValue() {
        if (!this.birthdayMonth || !this.birthdayDay) return '';
        return `${this.birthdayMonth.padStart(2, '0')}-${this.birthdayDay.padStart(2, '0')}`;
      },

      openBirthday() {
        this.openModal('birthday');
        this.$nextTick(() => this.$refs.birthdayDay?.focus());
      },

      closeBirthday() {
        this.showBirthday = false;
        this.closeModal();
      },

      async saveBirthday() {
        const birthday = this.birthdayValue();
        if (!birthday) {
          this.showStatus('Select both a day and month.', 'error');
          return;
        }

        const succeeded = await this.sendProfileWebhook('birthday', { birthday });
        if (!succeeded) return;

        this.$store.profile.birthday = birthday;
        this.closeBirthday();
        this.showStatus('Your birthday has been added.');
      },

      openPass() {
        this.openModal('pass');
        this.$nextTick(() => {
          this.$refs.passClose?.focus();
          this.renderQrCode();
        });
      },

      closePass() {
        this.showPass = false;
        this.closeModal();
      },

      async renderQrCode() {
        const holder = this.$refs.qrCode;
        if (!holder || holder.dataset.rendered === 'true') return;

        try {
          await window.FAFQRCode.render(holder, {
            text: String(config.memberCode || config.customerId || ''),
            width: 180,
            height: 180,
            colorDark: '#1E293A',
            colorLight: '#FFFFFF',
            correctLevel: 'M'
          });
          holder.dataset.rendered = 'true';
        } catch (error) {
          this.showStatus('Your membership QR code could not be loaded. Please try again.', 'error');
        }
      },

      openModal(type) {
        this.previousFocus = document.activeElement;
        this.lockPage();
        if (type === 'birthday') this.showBirthday = true;
        if (type === 'pass') this.showPass = true;
      },

      closeModal() {
        this.unlockPage();
        this.$nextTick(() => this.previousFocus?.focus?.({ preventScroll: true }));
      },

      lockPage() {
        window.FAFScrollLock?.lock('account-modal');
      },

      unlockPage() {
        window.FAFScrollLock?.unlock('account-modal');
      },

      trapModalFocus(event, refName) {
        const modal = this.$refs[refName];
        if (!modal) return;
        const focusable = Array.from(modal.querySelectorAll('button:not([disabled]), a[href], select:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'));
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      },

      async copyText(value, message = 'Copied') {
        try {
          if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(String(value));
          } else {
            const input = document.createElement('textarea');
            input.value = String(value);
            input.setAttribute('readonly', '');
            input.style.position = 'fixed';
            input.style.opacity = '0';
            document.body.appendChild(input);
            input.select();
            document.execCommand('copy');
            input.remove();
          }
          this.showStatus(message);
        } catch (error) {
          this.showStatus('Unable to copy. Please select the code manually.', 'error');
        }
      },

      openApp() {
        const started = Date.now();
        window.location.href = config.appScheme || 'fairfaxandfavor://home';
        window.setTimeout(() => {
          if (document.visibilityState === 'visible' && Date.now() - started < 3500 && config.appDownloadUrl) {
            window.location.href = config.appDownloadUrl;
          }
        }, 1800);
      },

      openWishlist() {
        if (this.$store.cart && this.$store.lazyPanel) {
          this.$store.cart.mode = 'wishlist';
          this.$store.lazyPanel.openPanel('push-cart');
        } else if (config.wishlistUrl) {
          window.location.href = config.wishlistUrl;
        }
      },

      scrollCarousel(refName, direction = 1) {
        const element = this.$refs[refName];
        if (!element) return;
        const distance = Math.max(240, element.clientWidth * 0.82) * direction;
        element.scrollBy({ left: distance, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }
    }));
});
